import { OBJECT_STRIDE, ObjectUniforms, UniformOffsets } from './ObjectUniforms.js';
import { USE_OUT_OF_CORE_TILE_MANAGER, Megabytes } from "../../../globals";
import { BufferManagerWebGPU } from '../../BufferManagerWebGPU';
import { RenderBatch } from '../../../scene/RenderBatch.js';

/** @import { MemoryBlock } from '../../../memory/MemoryBlock.js' */

// Estimated limit for maximum size of a batch. Used for conservative esimtation of required buffer sizes.
//
// Currently, using OutOfCoreTileManager can result in drawing objects with many
// instances. These don't fit in the current 512 MAX_BATCH limit and this results
// in an infinite loop in RenderBatchShim.forEachWGPU().
// HACKY FIX: Increase this limit to UINT16_MAX - the maximum number of fragments a BVH node can contain.
// A proper fix might be a mechanism to resume drawing on the same object, but
// starting at a different instance to avoid this infinite loop.
export const MAX_BATCH = () => USE_OUT_OF_CORE_TILE_MANAGER ? 65535 : 512;

/**
 * Attached to RenderBatches to find the corresponding UniformBuffer range where the
 * ObjectUniforms are stored. Can be seen as a span [startIndex, startIndex + length) in the GPU side array of entries.
 * @typedef {Object} ObjectUniformBufferRange
 * @property {MemoryBlock} memoryBlock // Allocated block for this range.
 * @property {number} startIndex       // start index of the range in the GPU buffer
 * @property {number} length           // number of items in the range
 */

/**
 * Manages one or more GPU-side buffers to store object uniforms. Each buffer can be seen as a GPU-side array of items,
 * whereby each item stores the uniform data for a single object.
 */
export class ObjectUniformBuffer {

  /** @type {GPUDevice} */
  #device;
  /** @type {ObjectUniforms} */
  #uniforms;

  /** @type {BufferManagerWebGPU} */
  #bufferManager; // Handles the core GPU memory management

  // Single GPUBuffer for fixed-size uniform storage (used for dynamic uniforms)
  /** @type {GPUBuffer|null} */
  #gpuBuffer = null;

  // MaterialUniformPool - manages shared material uniforms referenced by object uniforms
  #materials = null;

  #isFixedSize = false;

  /** @type {Map<RenderBatch, ObjectUniformBufferRange>} */
  #ranges = new Map();

  // @param {GPUDevice} device
  // @param {MaterialUniformBuffer} materials - Separate storage of material uniforms that are shared by multiple objects.
  //                                             Entries of this storage are referenced by the object uniforms.
  // @param {number} [fixedSizeBytes=0]  - If >0, a single buffer with a fixed size is allocated immediately. Otherwise, we allow dynamic alloc and free.
  constructor(
  device,
  materials,
  fixedSizeBytes = 0)
  {
    this.#device = device;
    this.#materials = materials;
    this.#isFixedSize = fixedSizeBytes > 0;

    // We need a CPU-side buffer to collect/store object uniforms before uploading them to the GPU.
    // This buffer is smaller than the full GPU-side UniformBuffer.
    // The CPU side buffer size is chosen to fit MaxItemsPerUpload objects. This values defines the maximum
    // possible number of objects for which we can add uniform values before flusing the data to GPU.
    const MaxItemsPerUpload = MAX_BATCH();
    this.#uniforms = new ObjectUniforms(MaxItemsPerUpload);

    // Used only for fixed size buffer (for per-frame uniforms)
    if (this.#isFixedSize) {
      this.#gpuBuffer = this.#device.createBuffer({
        label: 'object-uniform-buffer',
        size: fixedSizeBytes,
        // Usage type "storage" allows much larger buffers than "uniform"
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
      });
    } else {
      const bufferDesc = {
        label: 'uniform-buffer',
        size: Megabytes(128),
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
      };
      // Use a buffer allocator for dynamic buffer allocation
      this.#bufferManager = new BufferManagerWebGPU(this.#device, bufferDesc);
    }
  }

  dtor() {
    this.#gpuBuffer?.destroy();
    this.#gpuBuffer = null;
    this.#device = null;
    this.#materials = null;
    this.#bufferManager?.dtor();
    this.#bufferManager = null;
  }

  /**
   * @returns {number} Summed byteSize of all ObjectUniforms on GPU
   */
  byteSize() {
    if (!this.#isFixedSize) {
      return this.#bufferManager.computeUsedBytes().ubBytes;
    }
    return this.#gpuBuffer.size;
  }

  /**
   * Allocate a range in a GPU-side buffer to store uniforms for a range of fragments.
   *
   * Note:
   *  - The internally used allocator does not handle out-of-memory cases so far. We may consider a mechanism to
   *    fallback to dynamic uniforms. Currently, we just rely on OutOfCoreTileManager to make sure that we don't run out of memory.
   * @param {RenderBatch} batch - The render batch for which we need to store UniformBuffer entries.
   * @returns {ObjectUniformBufferRange|null}
   */
  allocUniformBufferRange(batch) {
    const rangeLength = batch.count;

    // Currently, a range with > 128MB uniforms would fail to allocate. However, the BVH construction currently restricts
    // to 65K fragments per node anyway.
    const maxRangeLength = Math.floor(this.#bufferManager.getDefaultBufferSize() / OBJECT_STRIDE);
    if (rangeLength > maxRangeLength) {
      console.error(`Requested range length ${rangeLength} exceeds maximum buffer size ${this.#bufferManager.getDefaultBufferSize()}`);
      return null;
    }

    const byteSize = OBJECT_STRIDE * rangeLength;
    const memoryBlock = this.#bufferManager.allocate(byteSize);

    // Reserve range in current GPU Buffer and return it.
    const range = {
      memoryBlock, // Corresponding MemoryBlock in BufferManager
      startIndex: memoryBlock.offset / OBJECT_STRIDE,
      rangeLength
    };
    this.#ranges.set(batch, range);
    return range;
  }

  /**
   * Free any uniform buffer memory associated with this RenderBatch.
   * @param {RenderBatch} batch
   */
  freeUniformBufferRange(batch) {
    const range = this.getRange(batch);
    if (!range) {
      return;
    }

    // The memoryBlock should usually exist - unless the allocation failed already before.
    if (range.memoryBlock) {
      this.#bufferManager.free(range.memoryBlock);
    }

    // Remove the range from the map
    this.clearRange(batch);
  }

  /**
   * Get the range associated with this RenderBatch if one exists.
   * @param {RenderBatch} batch
   * @returns {ObjectUniformBufferRange|undefined}
   */
  getRange(batch) {
    return this.#ranges.get(batch);
  }

  /**
   * Clear the mapping from this batch to an ObjectUniformBufferRange.
   * @param {RenderBatch} batch
   */
  clearRange(batch) {
    this.#ranges.delete(batch);
  }


  /**
   * Uploads the uniforms for a list of fragments to a range in the GPU-side buffer.
   * Each item in the UniformBuffer range corresponds to a fragmentId in the
   * given fragIds array.
   * @param {RenderBatch} batch
   * @param {number} srcRangeStart range within fragIds array for which we want to upload the uniforms
   * @param {number} rangeLength number of fragments to update. Must match with the length of the available GPUBuffer range.
   */
  updateRange(
  batch,
  srcRangeStart,
  rangeLength)
  {
    const range = this.getRange(batch);

    // Large ranges might exceed the CPU-side buffer. If so, we may have to
    // split into multiple upload steps.
    let itemsAdded = 0;
    while (itemsAdded < rangeLength) {

      // Check how many items we can fit into CPU buffer. This limits the number of items that we can upload in one go.
      const maxItems = this.#uniforms.length();

      // Determine how many items we upload in this loop cycle
      const itemsLeft = rangeLength - itemsAdded;
      const itemsToAdd = Math.min(itemsLeft, maxItems);

      // Write uniform data to the CPU buffer
      const srcOffset = srcRangeStart + itemsAdded;
      const dstOffset = 0; // Note that in the CPU buffer, we start at 0. Offsetting by dstRangeStart and added items is only relevant for the GPU upload.
      this.#writeUniforms(batch.frags, batch.getIndices(), srcOffset, itemsToAdd, dstOffset);

      // Determine start index of the GPU-buffer range that we write to.
      // I.e., the previously saved index 0 in the CPU-side buffer will be written to the GPU buffer at index gpuWriteIndex.
      const gpuItemIndex = range.startIndex + itemsAdded;

      // upload CPU uniform data to GPU buffer
      this.#uniforms.upload(this.#device, range.memoryBlock.buffer, itemsToAdd, gpuItemIndex);

      itemsAdded += itemsToAdd;
    }
  }

  /**
   * When using a fixed size, returns the gpu buffer used.
   * @returns {GPUBuffer}
   */
  getGPUBuffer() {
    console.assert(this.#isFixedSize,
    'Calling getGPUBuffer() is only valid when using a fixed size uniform buffer.');
    return this.#gpuBuffer;
  }

  /**
   * Set objectUniforms in cpu-side buffer for a single fragment.
   * @param {FragmentList} fragList
   * @param {number} fragId
   * @param {number} index - array index in the CPU side buffer where we collect data for uploading.
   **/
  setOneFragmentCPU(fragList, fragId, index) {
    // If this fragment uses a material that is not on GPU yet, acquire memory for the material too.
    // This is necessary to obtain a valid materialReference for the fragment.
    const material = fragList.getMaterial(fragId);
    const materialRef = this.#materials.acquireMaterial(material);

    // Write object uniforms to position 'index' in the CPU buffer
    this.#uniforms.setFromFragment(index, fragList, fragId, materialRef);
  }

  /**
   * Set and upload uniforms for a single fragment immediately.
   * @param {FragmentList} fragList
   * @param {number} fragId
   * @param {RenderBatch} batch - RenderBatch containing this fragment
   * @param {number} gpuItemIndex - Array index of the item to be changed within the GPU buffer
   **/
  setOneFragment(fragList, fragId, batch, gpuItemIndex) {
    // Write object uniforms to position 0 in the CPU buffer
    this.setOneFragmentCPU(fragList, fragId, 0);

    // Upload uniform at index 0 to the GPU buffer
    const range = this.getRange(batch);
    const gpuBuffer = range?.memoryBlock.buffer;
    gpuBuffer && this.#uniforms.upload(this.#device, gpuBuffer, 1, gpuItemIndex, 0);
  }

  /**
   * Set uniforms for one object, but only in the CPU side buffer without uploading.
   * Upload is usually done afterwards for a whole batch at once, even for per-frame uniforms.
   *  @param {THREE.Mesh} mesh
   *  @param {number}     objectIndex - array index in the CPU side buffer where we collect data for uploading.
   */
  setOneObjectCPU(mesh, objectIndex) {
    // Make sure that we allocated memory for the uniforms
    // of this material and obtain the materialRef that we
    // need for the objectUniforms to refer to this material.
    const materialRef = this.#materials.acquireMaterial(mesh.material);

    // Write object uniform data
    this.#uniforms.setFromObject(mesh, objectIndex, materialRef);
  }

  /**
   * Sets object uniforms in the CPU-side buffer. Similar to setOneObjectCPU, but filling up uniforms for several subsequent
   * objects at once and  data from an instanced mesh.
   *  @param {THREE.Mesh} instancedMesh - must have geometry with geom.numInstance value and prepared instance buffer.
   *  @param {number} itemOffset - index in the CPU side buffer where we start writing.
   */
  setObjectDataFromInstanceBuffer(instancedMesh, itemOffset) {

    // Make sure the material itself is on GPU and get the reference to it
    const material = instancedMesh.material;
    this.#materials.updateMaterial(material);
    const materialRef = this.#materials.getMaterialRef(material);

    const geometry = instancedMesh.geometry;
    this.#uniforms.setObjectDataFromInstanceBuffer(geometry, itemOffset, materialRef);
  }

  /**
   * Calling this is only needed if you used setOneObjectCPU. Uploads the CPU-side data to the GPU buffer.
   * @param {number} itemCount - Number of items to upload. We upload the range [0, itemCount) from CPU buffer.
   */
  writeToQueue(itemCount) {
    console.assert(this.#isFixedSize,
    'Calling writeToQueue() is only valid when using a fixed size uniform buffer.');
    this.#uniforms.upload(this.#device, this.#gpuBuffer, itemCount, 0, 0, true);
  }

  /**
   * Replace material for a single fragment.
   * @param {THREE.Material} material
   * @param {RenderBatch} batch - RenderBatch containing this fragment
   * @param {number} gpuItemIndex - Array index of the item to be changed within the GPU buffer
   */
  setMaterial(
  material,
  batch,
  gpuItemIndex)
  {
    // Custom pipeline materials use their own uniform handling and don't need
    // a materialRef in the ObjectUniforms buffer.
    if (material.needsCustomPipeline) {
      return;
    }

    // Make sure the material itself is on GPU and get the reference to it
    this.#materials.updateMaterial(material);
    const materialRef = this.#materials.getMaterialRef(material);

    // Write the reference to the material into the GPU UniformBuffer
    const range = this.getRange(batch);
    const gpuBuffer = range?.memoryBlock.buffer;
    gpuBuffer && this.#uniforms.uploadIntValue(this.#device, gpuBuffer, gpuItemIndex, UniformOffsets.materialReference, materialRef);
  }

  /**
   * Set transform for a single ObjectUniformBuffer item.
   * @param {FragmentList} fragList
   * @param {number} fragId
   * @param {RenderBatch} batch - RenderBatch containing this fragment
   * @param {number} gpuItemIndex - Array index of the item to be changed within the GPU buffer
   */
  updateTransform(
  fragList,
  fragId,
  batch,
  gpuItemIndex)
  {
    const range = this.getRange(batch);
    const gpuBuffer = range?.memoryBlock.buffer;
    gpuBuffer && this.#uniforms.uploadTransform(this.#device, gpuBuffer, gpuItemIndex, fragList, fragId);
  }

  /**
   * Sets and uploads the theming color uniform for the object at the specified index.
   * @param {THREE.Vector4} color The theming color vector of the fragment.
   * @param {RenderBatch} batch - RenderBatch containing this fragment
   * @param {number} gpuItemIndex - Array index of the item to be changed within the GPU buffer
   */
  setThemingColor(
  color,
  batch,
  gpuItemIndex)
  {
    const range = this.getRange(batch);
    const gpuBuffer = range?.memoryBlock.buffer;
    gpuBuffer && this.#uniforms.uploadThemingColor(this.#device, gpuBuffer, gpuItemIndex, color);
  }

  /**
   * Sets and uploads the theming color uniform for the object at the specified index.
   *  @param {number} flags - bitmask of FragmentFlagsGPU values
   *  @param {RenderBatch} batch - RenderBatch containing this fragment
   *  @param {number} gpuItemIndex Array index of the item to be changed within the GPU buffer
   */
  setFragmentFlags(flags, batch, gpuItemIndex) {
    const range = this.getRange(batch);
    const gpuBuffer = range?.memoryBlock.buffer;
    gpuBuffer && this.#uniforms.uploadIntValue(this.#device, gpuBuffer, gpuItemIndex, UniformOffsets.flags, flags);
  }

  /**
   * Direct access to ObjectUniforms
   * @returns {ObjectUniforms}
   */
  getUniformBuilder() {
    return this.#uniforms;
  }

  //
  // Private methods
  //

  // Write a range of fragment uniform data to CPU-buffer (for later upload) while assuming...
  //  1. The range fits into the remaining capacity of the CPU buffer of this.#uniformBuilder
  //  2. The range fits into the remaining capacity of the current GPU buffer
  // Note that the CPU-side transfer buffer (1.) is usually smaller than the whole GPU buffer (2.)
  #writeUniforms(
  fragList, // FragmentList
  fragIds, // Int32Array of fragment ids
  rangeStart, // start index of a range in fragIds
  rangeLength,
  dstOffset = 0 // start index where to write into the CPU buffer
  ) {
    for (let i = 0; i < rangeLength; i++) {
      // get fragId and material
      const srcIndex = rangeStart + i;
      const fragId = fragIds[srcIndex];
      const material = fragList.getMaterial(fragId);

      // Material may initially be undefined. In this case, materialRef will be unset at first.
      // Note that materialRef  0 has no special meaning but just means "whatever, it doesn't matter" in this context.
      // However, this is not a problem, because the fragment will not be rendered until the materialRef is set.
      let materialRef = 0;
      if (material) {
        // This also makes sure that materialRef exists if this is the first time we see this material.
        materialRef = this.#materials.acquireMaterial(material);
      }

      // Write object uniforms to position 'writeIndex' in the CPU buffer
      const writeIndex = dstOffset + i;
      this.#uniforms.setFromFragment(writeIndex, fragList, fragId, materialRef);
    }
  }
}